<template>
    <v-menu
        :key="menuKey"
        :model-value="open"
        location="bottom start"
        offset="6"
        :close-on-content-click="false"
        :scrim="false"
        @update:model-value="onOpenChange"
    >
        <template #activator="{ props: menuProps }">
            <span v-if="variant === 'ware-button'" class="d-inline-flex">
                <v-btn
                    v-bind="menuProps"
                    size="default"
                    color="yellow"
                    variant="outlined"
                    @click.stop
                >
                    <template v-if="appliedTripDurationLabel">
                        {{ $t("entries.tripDuration") }}:&nbsp;{{ appliedTripDurationLabel }}
                    </template>
                    <template v-else>
                        {{ $t("entries.distanceCalculator") }}
                    </template>
                </v-btn>
                <v-tooltip activator="parent" location="bottom">
                    {{
                        appliedTripDurationLabel
                            ? $t("entries.tripDurationTooltip")
                            : $t("entries.distanceCalculatorTooltip")
                    }}
                </v-tooltip>
            </span>

            <span v-else-if="variant === 'toolbar-button'" class="d-inline-flex">
                <v-btn
                    v-bind="menuProps"
                    density="compact"
                    color="primary"
                    variant="flat"
                    data-cy="publish-calculator-menu-button"
                    @click.stop
                >
                    <template v-if="appliedTripDurationLabel">
                        {{ $t("entries.tripDuration") }}:&nbsp;{{ appliedTripDurationLabel }}
                    </template>
                    <template v-else>
                        {{ $t("entries.distanceCalculator") }}
                    </template>
                </v-btn>
                <v-tooltip activator="parent" location="bottom">
                    {{
                        appliedTripDurationLabel
                            ? $t("entries.tripDurationTooltip")
                            : $t("entries.distanceCalculatorTooltip")
                    }}
                </v-tooltip>
            </span>

            <span
                v-else
                v-bind="menuProps"
                class="toolbar-left-group cursor-pointer"
                @click.stop
                @mousedown.stop
                @touchstart.stop
            >
                <span class="text-white">{{ $t("entries.tripDuration") }}:&nbsp;</span>
                <span class="text-yellow">{{ tripDurationLabel }}</span>
                <v-tooltip activator="parent" location="bottom">
                    {{ $t("entries.tripDurationTooltip") }}
                </v-tooltip>
            </span>
        </template>

        <TripDurationPanel
            ref="panel"
            :entry="entry"
            :show-distance-input="showDistanceInput"
            :show-apply-button="variant === 'ware-button'"
            :distance-km="distanceKm"
            :speed-kmh="speedKmh"
            :speed-readonly="speedReadonly"
            @update:distance-km="$emit('update:distanceKm', $event)"
            @update:speed-kmh="$emit('update:speedKmh', $event)"
            @apply="$emit('apply', $event)"
        />
    </v-menu>
</template>

<script>
import { formatDurationCompact } from "@/utils";
import { computeEtaBreakdown } from "@/utils/etaCalculation";
import TripDurationPanel from "./TripDurationPanel.vue";

export default {
    name: "TripDurationMenu",
    components: {
        TripDurationPanel,
    },
    props: {
        entry: {
            type: Object,
            default: null,
        },
        open: {
            type: Boolean,
            default: false,
        },
        distanceKm: {
            type: [String, Number],
            default: "",
        },
        showDistanceInput: {
            type: Boolean,
            default: false,
        },
        speedKmh: {
            type: [String, Number],
            default: "",
        },
        speedReadonly: {
            type: Boolean,
            default: false,
        },
        appliedTripDurationLabel: {
            type: String,
            default: "",
        },
        variant: {
            type: String,
            default: "ware-button",
            validator: (value) =>
                ["ware-button", "toolbar-button", "truck-label"].includes(value),
        },
    },
    emits: ["update:open", "update:speedKmh", "update:distanceKm", "apply"],
    computed: {
        menuKey() {
            const entryUid = this.entry?.uid ?? "publish";
            return `trip-duration-${entryUid}-${this.variant}`;
        },
        effectiveDistanceKm() {
            if (this.showDistanceInput) {
                return this.distanceKm;
            }
            return this.entry?.distance_km;
        },
        tripDurationLabel() {
            const speed = Number(this.speedKmh);
            if (!Number.isFinite(speed) || speed <= 0) return null;
            const breakdown = computeEtaBreakdown({
                distanceKm: this.effectiveDistanceKm,
                avgSpeedKmh: speed,
            });
            if (!breakdown) return null;
            return formatDurationCompact(breakdown.totalTripSeconds);
        },
    },
    methods: {
        onOpenChange(open) {
            if (!open && (this.variant === "ware-button" || this.variant === "toolbar-button")) {
                requestAnimationFrame(() => {
                    const panelEl = this.$refs.panel?.$el;
                    if (panelEl?.contains(document.activeElement)) {
                        this.$emit("update:open", true);
                        return;
                    }
                    this.$refs.panel?.tryApply();
                    this.$emit("update:open", false);
                });
                return;
            }
            this.$emit("update:open", open);
        },
    },
};
</script>
